import { CompanyBaseGet } from "./company-interfaces";
import { FileResponse } from "./file-upload-interfaces";

export interface BaseLocationGet {
    id: string | null;
    street: string | null;
    number: string | null;
    postal_code: string | null;
    city: string | null;
    province: string | null;
}

export interface PODLocationGet extends BaseLocationGet {
}

export interface PDRLocationGet extends BaseLocationGet {
}

export interface SupplierGet {
    id: string | null;
    name: string | null;
    vat_id: string | null;
    contact_number: string | null;
    email: string | null;
    contact_name: string | null;
    start_of_supply: string | null;
    end_of_supply: string | null;
}

export interface BaseInvoiceGet {
    id: string;
    created_at: string;
    file: FileResponse;
    company_id: string;
    location_id: string | null;
    status: InvoiceStatus;
    for_period: string | null;
    invoice_number: string | null;
    supplier_name: string | null;
    total_consumption: number | null;
    invoice_amount_net_actual: number | null;
    invoice_amount_gross_actual: number | null;
    total_taxes: number | null;
    tms_charges: number | null;
    is_consumption_ok: boolean;
    is_taxes_ok: boolean;
    is_invoice_amount_ok: boolean;
    is_vat_amount_ok: boolean;
    is_invoice_amount_gross_ok: boolean;
    total_discount_amount: number | null;
    vat_rate: number | null;
    vat_amount_actual: number | null;
    nontaxable_amount: number | null;
    consumption_class: string | null;

    visibility_status: VisibilityStatus;

    admin_notes: string | null;

    admin_approved: boolean;
    review_requested: boolean;

    expected_vat_rate: number | null;
    additional_charges: number | null;
}

export interface ElectricityPODCalculationData {
    total_consumption_calc: number | null;
    price_var_f1_calc: number | null;
    price_var_f2_calc: number | null;
    price_var_f3_calc: number | null;
    price_var_f0_calc: number | null;
    price_var_p_calc: number | null;
    price_var_po_calc: number | null;
    network_loss_f1: number | null;
    network_loss_f2: number | null;
    network_loss_f3: number | null;
    network_loss_f0: number | null;
    network_loss_p: number | null;
    network_loss_po: number | null;
    total_material_costs: number | null;
    energy_charge: number | null;
    power_charge: number | null;
    tms_charges_calc: number | null;
    total_taxes_calc: number | null;
    unit_pass_costs: number | null;
    total_unit_costs_f1: number | null;
    total_unit_costs_f2: number | null;
    total_unit_costs_f3: number | null;
    total_unit_costs_f0: number | null;
    total_unit_costs_p: number | null;
    total_unit_costs_po: number | null;
    total_commodity_costs: number | null;
    invoice_amount_net_calc: number | null;
    invoice_amount_gross_calc: number | null;
    vat_amount_calc: number | null;
    file_dispute_reasons: Array<Record<string, any>> | null;
}

export interface InvoiceElectricityPODGet {
    id: string | null;
    contract_type: ContractType | null;
    tariff_code: string | null;
    consumption_class: string | null;
    month_consumption_f1: number | null;
    month_consumption_f2: number | null;
    month_consumption_f3: number | null;
    month_consumption_f0: number | null;
    month_consumption_p: number | null;
    month_consumption_po: number | null;
    price_type: string | null;
    pun_price_f1: number | null;
    pun_price_f2: number | null;
    pun_price_f3: number | null;
    pun_price_f0: number | null;
    pun_price_p: number | null;
    pun_price_po: number | null;
    spread_f1: number | null;
    spread_f2: number | null;
    spread_f3: number | null;
    spread_f0: number | null;
    spread_p: number | null;
    spread_po: number | null;
    price_var_f1: number | null;
    price_var_f2: number | null;
    price_var_f3: number | null;
    price_var_f0: number | null;
    price_var_p: number | null;
    price_var_po: number | null;
    price_fix_f1: number | null;
    price_fix_f2: number | null;
    price_fix_f3: number | null;
    price_fix_p: number | null;
    price_fix_po: number | null;
    price_fix_f0: number | null;
    network_tension: string | null;
    tension_factor: number | null;
    power_kw_available: number | null;
    power_kw_contracted: number | null;
    power_kw_max_consumption: number | null;
    dispatch_costs: number | null;
    dispatching_factor: number | null;
    market_capacity_p: number | null;
    market_capacity_op: number | null;
    fixed_charge: number | null;
    energy_charge_price: number | null;
    power_charge_price: number | null;
    reactive_energy_costs: number | null;
    fixed_costs: number | null;
    additional_charges: number | null;
    total_discount_amount: number | null;
    taxrate_1: number | null;
    taxrate_2: number | null;
    is_price_var_f1_ok: boolean;
    is_price_var_f2_ok: boolean;
    is_price_var_f3_ok: boolean;
    is_price_var_f0_ok: boolean;
    is_price_var_p_ok: boolean;
    is_price_var_po_ok: boolean;
    is_tms_charges_ok: boolean;
    calc_data: ElectricityPODCalculationData | null;
    supplier: SupplierGet | null;
    pod_id: string | null;
    available_capacity: number | null;
    voltage: number | null;
    excise_taxes: number | null;
    vat_rate: number | null;
    vat_amount_actual: number | null;
    tms_charges: number | null;
    total_taxes: number | null;
    modulation_charge: number | null;
    additional_note_fields: AdditionalNoteFieldsElectricityGet | null;
}

export interface ElectricityInvoice extends BaseInvoiceGet {
    pods: InvoiceElectricityPODGet[]
}

export interface InvoiceElectricityAndCompanyGet extends ElectricityInvoice {
    company: CompanyBaseGet | null
    vat_id: string | null;
}

export interface GasPDRCalculationData {
    td_key: string | null;

    price_gas_var_calc: number | null;
    fixed_charge_gas_calc: number | null;
    material_costs_gas_variable_calc: number | null;
    material_costs_gas_fixed_calc: number | null;
    var_transportation_costs_calc: number | null;
    cummulated_gas_consumption: number | null;

    consumption_tier_1: number | null;
    consumption_tier_2: number | null;
    consumption_tier_3: number | null;
    consumption_tier_4: number | null;
    consumption_tier_5: number | null;
    consumption_tier_6: number | null;
    consumption_tier_7: number | null;
    consumption_tier_8: number | null;

    var_distribution_costs_tier1: number | null;
    var_distribution_costs_tier2: number | null;
    var_distribution_costs_tier3: number | null;
    var_distribution_costs_tier4: number | null;
    var_distribution_costs_tier5: number | null;
    var_distribution_costs_tier6: number | null;
    var_distribution_costs_tier7: number | null;
    var_distribution_costs_tier8: number | null;

    total_var_distribution_costs_calc: number | null;
    tms_gas_charges_calc: number | null;

    tax_consumption_scale1: number | null;
    tax_consumption_scale2: number | null;
    tax_consumption_scale3: number | null;
    tax_consumption_scale4: number | null;

    total_gas_taxes_calc: number | null;
    invoice_amount_net_calc: number | null;
    invoice_amount_gross_calc: number | null;
    vat_amount_calc: number | null;
}

export interface InvoiceGasPDRGet {
    id: string | null;
    spread: number | null;
    fixed_quota: number | null;
    forecast_index_value: number | null;

    pdr_id: string | null;
    pdr_region: string | null;
    pdr_province: string | null;
    pdr_community: string | null;
    tax_region: string | null;
    area_gas: string | null;
    REMI_type_gas: string | null;
    gas_meter_class: string | null;
    gas_intensity_class: string | null;
    month_gas_consumption: number;
    gas_perc_with_var_price: number | null;
    daily_gas_capacity: number | null;
    price_type_gas: string | null;
    variable_gas_consumption: number | null;
    fixed_gas_consumption: number | null;

    gas_market_index: string | null;
    gas_market_price: number | null;
    price_gas_var_actual: number | null;
    price_gas_fix: number | null;
    hcv_gas: number | null;

    daily_capacity_factor_gas: number | null;
    fixed_charge_gas_actual: number | null;

    material_cost_factor: number | null;
    material_costs_gas_variable_actual: number | null;
    material_costs_gas_fixed_actual: number | null;

    fixed_distribution_costs_gas_calc: number | null;
    fixed_distribution_costs_gas_actual: number | null;
    variable_transportation_fee: number | null;
    var_transportation_costs_actual: number | null;

    var_distribution_fee_tier1: number | null;
    var_distribution_fee_tier2: number | null;
    var_distribution_fee_tier3: number | null;
    var_distribution_fee_tier4: number | null;
    var_distribution_fee_tier5: number | null;
    var_distribution_fee_tier6: number | null;
    var_distribution_fee_tier7: number | null;
    var_distribution_fee_tier8: number | null;
    total_var_distribution_costs_actual: number | null;

    tms_gas_charges_actual: number | null;

    expected_exercise_tax_type_gas: string | null;
    exercise_tax_type_gas: string | null;
    tax_type_gas: string | null;
    taxrate_gas_scale1: number | null;
    taxrate_gas_scale2: number | null;
    taxrate_gas_scale3: number | null;
    taxrate_gas_scale4: number | null;
    total_gas_taxes_actual: number | null;
    additional_gas_charges: number | null;
    total_gas_discount_amount: number | null;

    cost_data: Record<string, any> | null;

    is_unit_prices_ok: boolean;
    is_material_costs_ok: boolean;
    is_distribution_costs_ok: boolean;
    is_transportation_costs_ok: boolean;

    vat_rate: number | null;
    vat_amount_actual: number | null;

    calc_data: GasPDRCalculationData | null;
    supplier: SupplierGet | null;
    additional_note_fields: AdditionalNoteFieldsGasGet | null;
}

export interface GasInvoice extends BaseInvoiceGet {
    pdrs: InvoiceGasPDRGet[]
}

export interface InvoiceGasAndCompanyGet extends GasInvoice {
    company: CompanyBaseGet | null;
    vat_id: string | null;
}

export interface BaseInvoiceUpdate {
    id: string
    location_id: string | null
    for_period: string | null
    invoice_number: string | null
    total_consumption: number | null
    invoice_amount_net_actual: number | null
    total_taxes: number | null
}

export interface BaseHistoricalData {
    for_period?: string;
    total_consumption?: number;
    invoice_amount?: number;
    total_taxes?: number;
}

export interface HistoricalDataElectricity extends BaseHistoricalData {
    month_consumption_f1?: number;
    month_consumption_f2?: number;
    month_consumption_f3?: number;

    energy_charge_price?: number;
    reactive_energy_costs?: number;
    tms_charges?: number;
    dispatch_costs?: number;
}

export interface HistoricalDataGas extends BaseHistoricalData {
    fixed_quota?: number;
    spread?: number;
    tms_charges?: number;
}

export enum InvoiceStatus {
    CREATED = "CREATED",
    IN_VALIDATION = "IN_VALIDATION",
    VALID = "VALID",
    DATA_MISSING = "DATA_MISSING",
    IN_PROCESSING = "IN_PROCESSING",
    DONE = "DONE",
    ERROR = "ERROR",
    IN_DISPUTED = "IN_DISPUTED",
    CALCULATION_FAILED = "CALCULATION_FAILED"
}

export enum ContractType {
    FASCIA = "FASCIA",
    MONO = "MONO",
    PICCO = "PICCO"
}

export enum PriceType {
    FIX = "FIX",
    VAR = "VAR",
    VAR_PUN = "VAR_PUN",
    VAR_PUNH = "VAR_PUNH"
}

export enum NetworkTensionType {
    BT = "BT",
    MT = "MT",
    AT = "AT"
}

export enum ConsumptionClass {
    Asos0 = "Asos0",
    Asos1 = "Asos1",
    Asos2 = "Asos2",
    Asos3 = "Asos3",
}

export enum InvoiceVariableStatus {
    OK = "OK",
    WARNING = "WARNING",
    ERROR = "ERROR",
    MANDATORY_FIELD_MISSING = "MANDATORY_FIELD_MISSING",
    OPTIONAL_FIELD_EMPTY = "OPTIONAL_FIELD_EMPTY",
}

// Enums
export enum FileType {
    contract = 'contract',
    invoice = 'invoice'
}

export enum CommodityType {
    electricity = 'electricity',
    gas = 'gas'
}

export interface Location {
    street?: string | null;
    number?: string | null;
    postal_code?: string | null;
    city?: string | null;
    province?: string | null;
    latitude?: number | null;
    longitude?: number | null;
}

export interface BaseLocation {
    street?: string | null;
    number?: string | null;
    postal_code?: string | null;
    city?: string | null;
    province?: string | null;
}

export type PODLocation = BaseLocation;
export type PDRLocation = BaseLocation;

export interface Supplier {
    name?: string | null;
    vat_id?: string | null;
    contact_number?: string | null;
    email?: string | null;
    contact_name?: string | null;
    start_of_supply?: string | null;
    end_of_supply?: string | null;
}

export interface InvoiceElectricityExtractedData {
    supplier_name?: string | null;
    total_taxes?: number | null;
    tms_charges?: number | null;
    contract_type?: string | null;
    tariff_code?: string | null;
    consumption_class?: string | null;
    month_consumption_f1?: number | null;
    month_consumption_f2?: number | null;
    month_consumption_f3?: number | null;
    month_consumption_f0?: number | null;
    month_consumption_p?: number | null;
    month_consumption_po?: number | null;
    price_type?: string | null;
    pun_price_f1?: number | null;
    pun_price_f2?: number | null;
    pun_price_f3?: number | null;
    pun_price_f0?: number | null;
    pun_price_p?: number | null;
    pun_price_po?: number | null;
    spread_f1?: number | null;
    spread_f2?: number | null;
    spread_f3?: number | null;
    spread_f0?: number | null;
    spread_po?: number | null;
    spread_p?: number | null;
    price_var_f1?: number | null;
    price_var_f2?: number | null;
    price_var_f3?: number | null;
    price_var_f0?: number | null;
    price_var_p?: number | null;
    price_var_po?: number | null;
    price_fix_f1?: number | null;
    price_fix_f2?: number | null;
    price_fix_f3?: number | null;
    price_fix_f0?: number | null;
    price_fix_p?: number | null;
    price_fix_po?: number | null;
    network_tension?: string | null;
    tension_factor?: number | null;
    power_kw_available?: number | null;
    power_kw_contracted: number | null;
    power_kw_max_consumption?: number | null;
    dispatch_costs?: number | null;
    dispatching_factor?: number | null;
    market_capacity_p?: number | null;
    market_capacity_op?: number | null;
    fixed_charge?: number | null;
    energy_charge_price?: number | null;
    power_charge_price?: number | null;
    reactive_energy_costs?: number | null;
    fixed_costs?: number | null;
    additional_charges?: number | null;
    total_discount_amount?: number | null;
    taxrate_1?: number | null;
    taxrate_2?: number | null;
    location?: PODLocation | null;
    supplier?: Supplier | null;
    pod_id?: string | null;
    available_capacity?: number | null;
    voltage?: number | null;
    excise_taxes?: number | null;
    vat_rate?: number | null;
    vat_amount_actual?: number | null;
}

export interface InvoiceGasExtractedData {
    total_taxes?: number | null;
    spread?: number | null;
    fixed_quota?: number | null;
    forecast_index_value?: number | null;
    pdr_id?: string | null;
    area_gas?: string | null;
    REMI_type_gas?: string | null;
    gas_meter_class?: string | null;
    gas_intensity_class?: string | null;
    month_gas_consumption?: number | null;
    gas_perc_with_var_price?: number | null;
    daily_gas_capacity?: number | null;
    price_type_gas?: string | null;
    variable_gas_consumption?: number | null;
    fixed_gas_consumption?: number | null;
    gas_market_index?: string | null;
    gas_market_price?: number | null;
    price_gas_var_actual?: number | null;
    price_gas_fix?: number | null;
    hcv_gas?: number | null;
    daily_capacity_factor_gas?: number | null;
    fixed_charge_gas_actual?: number | null;
    material_cost_factor?: number | null;
    material_costs_gas_variable_actual?: number | null;
    material_costs_gas_fixed_actual?: number | null;
    fixed_distribution_costs_gas_calc?: number | null;
    fixed_distribution_costs_gas_actual?: number | null;
    variable_transportation_fee?: number | null;
    var_transportation_costs_actual?: number | null;
    total_var_distribution_costs_actual?: number | null;
    tms_gas_charges_actual?: number | null;
    exercise_tax_type_gas?: string | null;
    tax_type_gas?: string | null;
    taxrate_gas_scale1?: number | null;
    taxrate_gas_scale2?: number | null;
    taxrate_gas_scale3?: number | null;
    taxrate_gas_scale4?: number | null;
    total_gas_taxes_actual?: number | null;
    additional_gas_charges?: number | null;
    total_gas_discount_amount?: number | null;
    cost_data?: Record<string, any> | null;
    location?: PDRLocation | null;
    supplier?: Supplier | null;
    var_distribution_fee_tier1?: number | null;
    var_distribution_fee_tier2?: number | null;
    var_distribution_fee_tier3?: number | null;
    var_distribution_fee_tier4?: number | null;
    var_distribution_fee_tier5?: number | null;
    var_distribution_fee_tier6?: number | null;
    var_distribution_fee_tier7?: number | null;
    var_distribution_fee_tier8?: number | null;
    vat_rate?: number | null;
    vat_amount_actual?: number | null;
}

export interface InvoiceElectricityCommonData {
    for_period?: string | null;
    invoice_number?: string | null;
    total_consumption?: number | null;
    invoice_amount_net_actual?: number | null;
    invoice_amount_gross_actual?: number | null;
    consumption_class?: string | null;
    additional_charges?: number | null;
    total_discount_amount?: number | null;
    vat_rate: number | null;
    vat_amount_actual: number | null;
    nontaxable_amount: number | null;
    admin_notes?: string | null;
}

export interface InvoiceGasCommonData extends InvoiceElectricityCommonData { }

export interface InvoiceElectricityDataItem {
    location: Location;
    invoice_data: InvoiceElectricityCommonData;
    pod_data: InvoiceElectricityExtractedData[];
    historical_data: HistoricalDataElectricity[];
}

export interface InvoiceGasDataItem {
    location: Location;
    invoice_data: InvoiceGasCommonData;
    pdr_data: InvoiceGasExtractedData[];
    historical_data: HistoricalDataGas[];
}

export interface InvoiceElectricityData {
    id: string;
    processed_at: string;
    type: CommodityType;
    file_type: FileType;
    data: InvoiceElectricityDataItem;
}

export interface InvoiceGasData {
    id: string;
    processed_at: string;
    type: CommodityType;
    file_type: FileType;
    data: InvoiceGasDataItem;
}

export interface BaseLocationViewUpdate {
    supplier_id: string | null;
    supplier: string | null;
    vat_rate: number | null;
    start_of_supply: string | null;
    end_of_supply: string | null;
}

export interface LocationViewUpdatePOD extends BaseLocationViewUpdate {
    pod_id: string | null;
    excise_taxes: number | null;
    available_capacity: number | null;
    tension: string | null;
}

export interface LocationViewUpdatePDR extends BaseLocationViewUpdate {
    pdr_id: string | null;
    remi: string | null;
}

export enum VisibilityStatus {
    READY_FOR_USER = "READY_FOR_USER",

    UNDER_ADMIN_REVIEW = "UNDER_ADMIN_REVIEW",
    UNDER_SILENT_ADMIN_REVIEW = "UNDER_SILENT_ADMIN_REVIEW"
}

export interface ReviewUpdate {
    visibility_status: VisibilityStatus
}

export interface AdminChangesLog {
    message?: string;
    // Format: { "field_name": { "old": oldValue, "new": newValue } }
    changes_summary?: {
        [fieldName: string]: {
            old: any;
            new: any;
        };
    };
}

export interface AdminChangesSendEmail {
    invoice_number?: string;
}

// Additional fields in invoice review, for easier editing by admins
export interface BaseAdditionalNoteFieldsCreate {
    invoice_charges: number | null;
}

export interface AdditionalNoteFieldsElectricityCreate extends BaseAdditionalNoteFieldsCreate {
    total_material_cost: number | null;

    network_loss_f0: number | null;
    network_loss_f1: number | null;
    network_loss_f2: number | null;
    network_loss_f3: number | null;
    network_loss_p: number | null;
    network_loss_po: number | null;

    dispatch_and_marketing_capacity: number | null;

    energy_charge: number | null;
    power_charge: number | null;
}

export interface AdditionalNoteFieldsGasCreate extends BaseAdditionalNoteFieldsCreate { }

export interface AdditionalNoteFieldsElectricityGet extends AdditionalNoteFieldsElectricityCreate {
    id: string;
}

export interface AdditionalNoteFieldsGasGet extends AdditionalNoteFieldsGasCreate {
    id: string;
}
